import { EV } from '../core/eventbus.js';

/**
 * 对讲机：按住说话，松手发一条语音，屋里在线的人当场听见。
 *
 * 走的是与位置、聊天同一条通道（本机多开走 BroadcastChannel，跨设备走 MQTT），
 * 一句话说完就发、听完就丢：不写数据库、不进书架、不碰服务器那点 KV 额度。
 * 没连房间时按了也没用，所以上来先提醒「先串门」。
 */

const MAX_MS = 15000;          // 一条最长 15 秒
const MIN_MS = 350;            // 短于这个当误触，不发出去
const MAX_BYTES = 96 * 1024;   // 原始音频上限（opus 24kbps 下 15 秒约 45KB）
const MAX_QUEUE = 4;           // 排队待播的条数，再多就丢最旧的
const RELEASE_AFTER = 12000;   // 说完多久把麦克风交回去（浏览器那个录音红点会灭）

// Safari 用 mp4，Chrome / Edge / Firefox 用 webm+opus，逐个问过去
const CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

function pickMime() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  for (const m of CANDIDATES) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* 这个不支持就试下一个 */ }
  }
  return '';
}

/** ArrayBuffer -> base64（分块拼，免得一次 apply 撑爆参数上限） */
function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return typeof btoa === 'function' ? btoa(s) : '';
}

function fromB64(b64) {
  const bin = typeof atob === 'function' ? atob(b64) : '';
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

export class Walkie {
  /**
   * @param {import('../core/engine.js').Engine} engine
   * @param {import('./net.js').NetProxy} net
   * @param {{toast?: (text: string, kind?: string) => void}} [opts]
   */
  constructor(engine, net, opts = {}) {
    this.engine = engine;
    this.net = net;
    this.toast = opts.toast || (() => {});

    this.recording = false;
    this.sending = false;
    this.mic = null;        // MediaStream，说完过一会儿才放掉，连着说才不卡
    this.rec = null;
    this.chunks = [];
    this.mime = '';
    this.startedAt = 0;
    this.stopTimer = 0;
    this.idleTimer = 0;
    this.queue = [];
    this.playing = false;

    net.on((msg) => { if (msg && msg.t === 'voice') this._incoming(msg); });
  }

  static supported() {
    return typeof navigator !== 'undefined'
      && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      && typeof MediaRecorder !== 'undefined';
  }

  _set(state) { this.engine.events.emit(EV.VOICE_STATE, { state }); }

  /** 按住：开始录。麦克风授权只在第一次（或麦克风已交回后）询问 */
  async start() {
    if (this.recording || this.sending) return;
    if (!Walkie.supported()) { this.toast('这个浏览器不让录音，换 Chrome / Edge 试试', 'warn'); return; }
    if (!this.net.open) { this.toast('先串门，屋里没人就没人听见', 'warn'); return; }

    clearTimeout(this.idleTimer);
    try {
      if (!this.mic) {
        this.mic = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      }
    } catch (err) {
      const why = err && err.name === 'NotAllowedError' ? '你没点头允许' : `（${(err && err.name) || '打不开'}）`;
      this.toast('开不了麦克风：' + why, 'warn');
      return;
    }

    try {
      this.chunks = [];
      this.mime = pickMime();
      const opt = { audioBitsPerSecond: 24000 };   // 说话用不着高码率，省流量
      if (this.mime) opt.mimeType = this.mime;
      const rec = new MediaRecorder(this.mic, opt);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) this.chunks.push(e.data); };
      rec.onstop = () => { this._finish(); };
      rec.start();
      this.rec = rec;
      this.recording = true;
      this.startedAt = Date.now();
      this.stopTimer = setTimeout(() => {
        this.toast('一条最多说 15 秒，已经替你发出去了', 'info');
        this.stop();
      }, MAX_MS);
      this._set('rec');
    } catch (err) {
      this.recording = false;
      this.toast('录不起来：' + ((err && err.message) || '未知原因'), 'warn');
      this._set('idle');
    }
  }

  /** 松手：停止录音，够长就发一条 */
  stop() {
    if (!this.recording || !this.rec) return;
    clearTimeout(this.stopTimer);
    try {
      this.rec.stop();   // 结束事件在 onstop 里接着处理
    } catch {
      this.recording = false;
      this._set('idle');
    }
  }

  /** 松开 / 超时后收尾：组装、校验、发送 */
  async _finish() {
    const ms = Date.now() - this.startedAt;
    const mime = (this.chunks[0] && this.chunks[0].type) || this.mime || 'audio/webm';
    const blob = new Blob(this.chunks, { type: mime });
    this.chunks = [];
    this.rec = null;
    this.recording = false;
    this._set('idle');
    this._releaseSoon();

    if (ms < MIN_MS || blob.size < 256) { this.toast('太短了，没发出去', 'warn'); return; }
    if (blob.size > MAX_BYTES) {
      this.toast(`这段有 ${Math.round(blob.size / 1024)}KB，太长了，没发出去`, 'warn');
      return;
    }

    this.sending = true;
    this._set('send');
    try {
      const b64 = toB64(await blob.arrayBuffer());
      if (!b64) { this.toast('打包失败，没发出去', 'warn'); return; }
      const name = (this.engine.me && this.engine.me.name) || '居者';
      this.net.send('voice', { b64, mime, ms, name });
      this.toast(`说了一段话（${(ms / 1000).toFixed(1)} 秒）`, 'good');
    } catch {
      this.toast('发送失败', 'warn');
    } finally {
      this.sending = false;
      this._set('idle');
    }
  }

  /** 说完歇一会儿就把麦克风交回去，免得浏览器的录音红点一直亮着 */
  _releaseSoon() {
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => {
      if (this.mic) { for (const t of this.mic.getTracks()) t.stop(); }
      this.mic = null;
    }, RELEASE_AFTER);
  }

  /** 收到别人的一条语音：解码成临时地址，排着队播 */
  _incoming(msg) {
    if (!msg || !msg.b64) return;
    if (msg.from && msg.from === this.net.selfId) return;   // 自己发的那条回声

    let bytes;
    try { bytes = fromB64(msg.b64); } catch { return; }

    // 没有 Audio / createObjectURL 的环境（如 Node 冒烟测试）只报个信，不播
    const canPlay = typeof Audio !== 'undefined'
      && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
    if (canPlay) {
      const blob = new Blob([bytes], { type: msg.mime || 'audio/webm' });
      const url = URL.createObjectURL(blob);
      if (this.queue.length >= MAX_QUEUE) URL.revokeObjectURL(this.queue.shift().url);
      this.queue.push({ url, ms: msg.ms || 0 });
    }

    this.engine.events.emit(EV.VOICE_IN, { name: msg.name || '有人', ms: msg.ms || 0, uid: msg.uid || '' });
    if (canPlay && !this.playing) this._next();
  }

  _next() {
    const item = this.queue.shift();
    if (!item) { this.playing = false; return; }
    this.playing = true;
    const a = new Audio(item.url);
    a.volume = 0.9;
    const done = () => { URL.revokeObjectURL(item.url); this._next(); };
    a.onended = done;
    a.onerror = done;
    // 浏览器在用户还没跟页面打过交道时会拦下自动播放，那种情况给个提示就停
    a.play().catch(() => {
      URL.revokeObjectURL(item.url);
      this.playing = false;
      this.toast('浏览器拦了声音，点一下画面就能听见了', 'warn');
    });
  }

  /** 离开房间 / 关页面：手上的东西全撒开，不留麦克风 */
  dispose() {
    this.stop();
    clearTimeout(this.idleTimer);
    if (this.mic) { for (const t of this.mic.getTracks()) t.stop(); }
    this.mic = null;
    for (const item of this.queue) URL.revokeObjectURL(item.url);
    this.queue.length = 0;
    this.playing = false;
  }
}
